import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { HeroConfig } from './HeroWrapper';

type HeroContextType = {
  heroConfig: HeroConfig;
  setHeroConfig: (config: HeroConfig) => void;
};

const HeroContext = createContext<HeroContextType | undefined>(undefined);

export const HeroProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [heroConfig, setHeroConfig] = useState<HeroConfig>({});

  const setHeroConfigCallback = useCallback((config: HeroConfig) => {
    setHeroConfig(config);
  }, []);

  const value = useMemo(
    () => ({ heroConfig, setHeroConfig: setHeroConfigCallback }),
    [heroConfig, setHeroConfigCallback],
  );

  return <HeroContext.Provider value={value}>{children}</HeroContext.Provider>;
};

export const useHeroConfig = () => {
  const context = useContext(HeroContext);
  if (!context) {
    // Return a no-op when not within provider (for signed-out routes)
    return { heroConfig: {}, setHeroConfig: () => {} };
  }
  return context;
};

// Component to set hero config from routes
export const HeroConfigSetter: React.FC<PropsWithChildren<{ config: HeroConfig }>> = ({ config, children }) => {
  const context = useContext(HeroContext);

  // Apply hero config before paint to avoid one-frame flashes between routes.
  useLayoutEffect(() => {
    // Only update config if we're inside a provider
    if (context) {
      context.setHeroConfig(config);
    }
  }, [config, context]);

  return <>{children}</>;
};
